import { type ReactNode } from "react";
import type { Action, Environment, Resource, Scope } from "@shield-acl/core";
import { useCan } from "./hooks/useCan";
import { useCanAsync } from "./hooks/useCanAsync";
import { useChecks, anyOf, allOf } from "./hooks/useChecks";
import type { CheckSpec } from "./hooks/internal";

export interface CanProps {
  action: Action;
  /** Nome/tipo do recurso (matching). */
  resource?: Resource;
  /** Instância do recurso (conditions ABAC). */
  record?: unknown;
  /** Override do scope do Provider. */
  scope?: Scope;
  environment?: Environment;
  fallback?: ReactNode;
  children: ReactNode;
}

/** Renderiza children quando o usuário TEM a permissão. */
function CanBase({
  action,
  resource,
  record,
  scope,
  environment,
  fallback = null,
  children,
}: CanProps) {
  const allowed = useCan(action, resource, { record, scope, environment });
  return <>{allowed ? children : fallback}</>;
}

/** Renderiza children quando o usuário NÃO tem a permissão. */
export function Cannot({
  action,
  resource,
  record,
  scope,
  environment,
  children,
}: Omit<CanProps, "fallback">) {
  const allowed = useCan(action, resource, { record, scope, environment });
  return <>{allowed ? null : children}</>;
}

export interface CanBatchProps {
  checks: CheckSpec[];
  fallback?: ReactNode;
  children: ReactNode;
}

function toMap(checks: CheckSpec[]): Record<string, CheckSpec> {
  const map: Record<string, CheckSpec> = {};
  checks.forEach((c, i) => {
    map[String(i)] = c;
  });
  return map;
}

/** Renderiza se QUALQUER checagem passar. */
function CanAny({ checks, fallback = null, children }: CanBatchProps) {
  const result = useChecks(toMap(checks));
  return <>{anyOf(result) ? children : fallback}</>;
}

/** Renderiza se TODAS as checagens passarem. */
function CanAll({ checks, fallback = null, children }: CanBatchProps) {
  const result = useChecks(toMap(checks));
  return <>{allOf(result) ? children : fallback}</>;
}

export interface CanAsyncProps extends CanProps {
  pending?: ReactNode;
}

/** Verificação assíncrona com estado de carregamento. */
function CanAsync({
  action,
  resource,
  record,
  scope,
  environment,
  pending = null,
  fallback = null,
  children,
}: CanAsyncProps) {
  const { allowed, loading } = useCanAsync(action, resource, {
    record,
    scope,
    environment,
  });
  if (loading) return <>{pending}</>;
  return <>{allowed ? children : fallback}</>;
}

/**
 * `<Can>` com composição namespaced: `<Can.Any>`, `<Can.All>`, `<Can.Async>`.
 */
export const Can = Object.assign(CanBase, {
  Any: CanAny,
  All: CanAll,
  Async: CanAsync,
});
